/**
 * @lines98/client/screens/result-screen — экран итогов партии (после конца игры).
 *
 * РАСКЛАДКА — ОБЩИЙ «ПОСТАМЕНТ» (styles/postament.css), как board / daily
 * (owner-directive 2026-05-29 «правила в стволе, экраны — ветки»). Экран
 * ДОСКОЦЕНТРИЧНЫЙ (owner-directive 2026-05-29): в центре — ФИНАЛЬНАЯ доска партии
 * (статичный снимок, `lib/static-board.ts`), вокруг — постамент-обвязка:
 *   · header   — заголовок + «?» (описательное в портрете уходит в попап);
 *   · board    — финальная доска (некликабельная), размер = эталон доски;
 *   · secondary— KPI отдельными блоками: Очки, Ходы (+ daily: «До новой доски»
 *                таймер) + статус-строка вердикта сервера (daily);
 *   · actions  — кнопки (общий action-rail kit: autofit + компакт в ландшафте).
 *
 * Финальная доска БЕСПЛАТНА: всё состояние партии (`engineState.grid`) уже в
 * памяти браузера сразу после конца игры — ни сервер, ни заглушка не нужны (для
 * casual И для доски дня). Если бы экран открыли без engineState (deeplink — не
 * поддерживается) — bounce на свежую доску.
 *
 * ЛОГИКА (несущая, сохранена при переходе на постамент): для daily — submit на
 * сервер (board-screen уже отправил; этот экран показывает вердикт + «Повторить
 * отправку» при сетевой ошибке), кэш дня, OAuth-вход (casual, через попап
 * Google/Discord — owner-decision 2026-05-29).
 */

import {
  DAILY_GOLD_MULTIPLIER,
  GOLD_PER_POINT_THRESHOLD,
  SHARDS_MIN_CLEAR,
  SHARDS_MODE_BASE,
} from '@lines98/shared/config/meta';
import type { FinishReason, GameMode, MetaFaucetSummary } from '@lines98/shared/types/game';

import { mountInfoPopover, type InfoPopoverHandle } from '../components/info-popover.js';
import { ACTION_RAIL_FILL } from '../lib/action-rail.js';
import { attachBoardAreaFit, type BoardAreaFitHandle } from '../lib/board-area-fit.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import { attachTitleFit } from '../lib/title-fit.js';
import { mountBoardView, type BoardViewHandle } from '../lib/board-view.js';
import { attachLiveAppearance, type LiveAppearanceHandle } from '../lib/board-appearance.js';
import { getClientPrefs } from '../lib/client-prefs.js';
import { attachCompactRail, type CompactRailHandle } from '../lib/compact-rail.js';
import { attachRailVerticalFit } from '../lib/rail-vertical-fit.js';
import { mountRewardBlock, type RewardBlockHandle } from '../lib/reward-block.js';
import {
  mountHelperUnlockOverlay,
  type HelperUnlockOverlayHandle,
} from '../lib/helper-unlock-overlay.js';
import {
  mountAchievementResultWindow,
  type AchievementResultWindowHandle,
} from '../lib/achievement-result-window.js';
import { copyShareLink } from '../lib/share-link.js';
import { t } from '../lib/i18n/index.js';
import { FS_BUTTON_MIN_PX } from '../lib/viewport-sizer.js';
import { applyDailyCacheDiscipline } from '../lib/daily-finish.js';
import { snapshotFinalBoard } from '../lib/daily-final-state.js';
import {
  isDemoAchievementWindow,
  isDemoAchievementWindowReduced,
  isVfitDebug,
} from '../lib/dev-flags.js';
import { formatHms, msUntilNextRollover } from '../services/countdown.js';
import {
  submitFinishedDailyGame,
  submitFinishedAllTimeGame,
  type SubmitResult,
} from '../services/game-submit.js';
import { openModePreview } from '../lib/mode-preview.js';
import {
  getGrid,
  getLines,
  getNext,
  getScore,
  useGameStore,
  type GameStoreState,
} from '../store/game-store.js';
import { showToast } from '../ui/toast.js';
import type { ScreenHandle } from './board-screen.js';

/**
 * Описательная копия для итога доски дня. Один источник — рендерится в «?»-попапе
 * (портрет) / ⓘ-попапе (компактный rail). Локализована через `t()` ЛЕНИВО (функция,
 * не const), чтобы язык резолвился в момент рендера попапа, а не на import.
 */
function resultDescriptiveCopy(): string {
  return t('game.result.descDaily');
}

/**
 * Тело «!»-попапа на кнопке «Поделиться» — ОДНА чёткая строка (owner-directive
 * 2026-05-30): отправить ссылку можно сейчас, но другие увидят реплей только после
 * закрытия доски дня (динамический таймер до 21:00 UTC).
 */
function buildShareCountdownBody(): string {
  const ms = msUntilNextRollover(Date.now());
  if (ms <= 0) return t('game.result.shareCountdownNow');
  return t('game.result.shareCountdown', { hms: formatHms(ms) });
}

/**
 * Лейблы причин завершения (result-screen) — run-persistence-lifecycle.md §7.2 (T-D).
 * RU-дефолты (P1-3); owner финализирует формулировки на плейтесте. `new_game` +
 * `timeout` добавлены RP-7 (`timeout` зарезервирован — таймера нет, но лейбл готов).
 * `restart` (restart-full-completion.md P1-1) — «Новая игра» mid-game, теперь полный
 * финиш с этим экраном; legacy `new_game` (тихий submit) клиентом более не эмитится.
 */
function finishReasonLabel(reason: FinishReason): string {
  const key: Record<FinishReason, string> = {
    board_full: 'game.result.finish.boardFull',
    player_finish: 'game.result.finish.playerFinish',
    rollover: 'game.result.finish.rollover',
    new_game: 'game.result.finish.newGame',
    restart: 'game.result.finish.restart',
    timeout: 'game.result.finish.timeout',
  };
  return t(key[reason]);
}

/** Лейблы reject-причин (reject ПОСЛЕ серверного pre-claim — попытка дня списана). */
function rejectReasonLabel(reason: string): string {
  const key: Record<string, string> = {
    invalid_seed: 'game.result.reject.invalidSeed',
    invalid_move: 'game.result.reject.invalidMove',
    unknown: 'game.result.reject.unknown',
  };
  const k = key[reason];
  return k ? t(k) : reason;
}

export function mountResultScreen(host: HTMLElement): ScreenHandle {
  const s0 = useGameStore.getState();
  // Fallback: без engineState (deeplink — не поддерживается) — на свежую доску.
  if (!s0.engineState) {
    useGameStore.getState().startNewGame();
    return { unmount: () => {} };
  }

  const isDaily = s0.context.kind === 'daily';
  // Non-daily server-scored game (extra-modes, ADR-0013): the SAME result surface
  // (§5.3 reuse), with a verified-verdict status (like daily — the all_time submit
  // also sets `dailySubmitResult`) but a non-daily action set («Играть ещё» same
  // mode / «К лидерборду режима» / share always-open). Both daily and all_time are
  // «server-scored» → share the verdict-dynamic + status row.
  const isAllTime = s0.context.kind === 'all_time';
  const isServerScored = isDaily || isAllTime;

  host.innerHTML = '';
  const root = document.createElement('div');
  // .postament-screen — общий скелет раскладки; .result-screen оставляет ТОЛЬКО
  // result-specific внешний вид (KPI-блоки, статус, попап входа).
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'result-screen postament-screen screen-root';
  // REVEAL-GATE (white-flash fix B, owner-playtest 2026-06-24): монтируем СКРЫТО
  // (opacity:0 в потоке — result.css), чтобы доска-снимок растеризовалась и раскладка
  // постамента устаканилась ПОКА скрыто; атрибут снимаем double-rAF в конце mount →
  // контент проявляется целиком, без мелькания пустой структуры. Канон «render hidden,
  // reveal when ready», как start/mode-select.
  root.dataset.mounting = '';
  root.innerHTML = template(s0);
  host.appendChild(root);

  // --- Финальная доска через ЕДИНЫЙ модуль «Доска» (lib/board-view) ---
  // Статичный снимок партии из памяти: 9×9 + плашка (Очки + Ходы + 3 след. шара).
  // Та же реализация, что игра/реплей/превью — никакого отдельного рендера доски.
  let boardView: BoardViewHandle | null = null;
  let boardAppearance: LiveAppearanceHandle | null = null;
  // Fit доски-снимка под доступную board-track постамента (lib/board-area-fit):
  // на width-bound портрете с reward-рядом доска-эталон переполняла свой 1fr-трек и
  // НАЕЗЖАЛА на блок добычи (SS-1509-класс — 320×480/768×1024). Модуль ужимает
  // ТОЛЬКО эту доску-снимок (scoped --cell-size на host), эталон :root / игровая
  // доска не трогаются. refit() вызовем после монтирования reward-ряда.
  let boardAreaFit: BoardAreaFitHandle | null = null;
  const boardArea = root.querySelector<HTMLElement>('.result-board-area');
  if (boardArea) {
    boardView = mountBoardView(boardArea, { interactive: false, ariaLabel: t('game.result.boardAria') });
    // Оформление игрока — финальная доска отрисована в выбранной палитре+материале
    // (AC-2 «применение ко всем поверхностям доски»). Реактивно: смена в drawer
    // (можно открыть с результата) перекрашивает снимок без пере-рендера.
    boardAppearance = attachLiveAppearance(boardView.frame);
    boardView.setScore(getScore(s0));
    boardView.setMoves(s0.moves.length);
    // Режим «Предсказание»: 3 следующих шара видны И в нише, И маленькими
    // призраками на клетках, где они появились бы (owner-directive 2026-05-30 —
    // финальная доска показывается ровно как игровая предсказания). В режиме
    // «вслепую» подсказок нет.
    const predict = s0.mode === 'predict';
    const next = getNext(s0);
    boardView.renderNiche(next, s0.mode !== 'blind');
    const grid = getGrid(s0);
    if (grid) boardView.renderStaticGrid(grid, predict ? next : undefined);
    // frame доски смонтирован → привязываем fit-под-область (см. выше).
    boardAreaFit = attachBoardAreaFit(boardArea);
  }

  // --- Попапы ---
  const popovers: InfoPopoverHandle[] = [];

  const attachPopovers = (): void => {
    while (popovers.length > 0) popovers.pop()!.unmount();
    // «?» — описательное у заголовка (портрет; в rail скрыт постаментом).
    const titleline = root.querySelector<HTMLElement>('.result-titleline');
    if (titleline) {
      popovers.push(
        mountInfoPopover({
          title: isDaily ? t('game.result.titleDaily') : t('game.result.titleCasual'),
          bodyText: describeResult(useGameStore.getState()),
          triggerHost: titleline,
          variant: '?',
        }),
      );
    }
    // «!» — countdown доступности общего реплея, внутри «Поделиться» (daily).
    const shareSlot = root.querySelector<HTMLElement>('.result-share-info-slot');
    if (shareSlot) {
      popovers.push(
        mountInfoPopover({
          title: t('game.result.shareInfoTitle'),
          bodyDynamic: buildShareCountdownBody,
          triggerHost: shareSlot,
          variant: '!',
        }),
      );
    }
  };

  wire(root);
  attachPopovers();

  // (Вход Google/Discord на итоге убран: кнопки «Войти» нет — навигация/вход уедут
   // в будущий глобальный рейл. Мёртвый signin-код удалён, owner-cleanup 2026-05-30.)

  // --- Авто-подбор текста (как board): заголовок + значения KPI ---
  const autofit: AutofitHandle = attachAutofit(root, [
    // Кнопки. stacked (2-кол сетка): line-mode с FLOOR читаемости (13px) — если
    // лейбл не влезает одной строкой при floor, переносится ПО СЛОВАМ (CSS
    // white-space:normal), а НЕ уходит ниже floor (P2 §0.A; §8.10 B/C). rail: как
    // общий kit (line, uniform, fillRatio). uniform — единый размер на группу.
    { selector: '.result-actions .btn', mode: 'line', uniform: true, fillRatio: ACTION_RAIL_FILL, capToDesign: true, minPx: FS_BUTTON_MIN_PX, onlyLayout: 'stacked' },
    { selector: '.result-actions .btn', mode: 'line', uniform: true, fillRatio: ACTION_RAIL_FILL, capToDesign: true, minPx: FS_BUTTON_MIN_PX, onlyLayout: 'rail' },
  ]);
  // Заголовок — ОБЩИЙ модуль lib/title-fit (единое правило размера на всех экранах,
  // owner-directive 2026-05-30): привязка к доске + fill 0.7 + capToDesign.
  const titleFit = attachTitleFit(root);

  // --- Компакт боковой панели (ландшафт): узкий rail → ⓘ/☰. Autofit кнопок —
  // выше (свой, чтобы в stacked был floor+перенос, а не line без floor из kit). ---
  const actionRail = attachResultActionRail(root, isDaily);

  // [ВРЕМЕННАЯ ДИАГНОСТИКА — снять после починки realme] плашка с замером для
  // фото owner'ом (он код не читает). Активна только при ?vfitdebug=1.
  attachVfitDebug(root);

  // (Таймер «До новой доски» убран — owner-directive 2026-05-30; живёт на превью.)

  // --- Блок добычи меты (Срез 1b, meta-jeweler-surfaces.md §4.1) ---
  // Server-scored партия несёт серверную `meta`-сводку в verified-результате. Когда
  // она приходит — монтируем lib/reward-block в `.result-reward-area` (ОДИН раз). Под
  // настройкой `showRewardScreen` (owner: выключаемо; награды всё равно начислены
  // сервером). Реактивен той же подпиской, что вердикт (meta едет на том же verified).
  let rewardBlock: RewardBlockHandle | null = null;
  const maybeMountReward = (s: GameStoreState): void => {
    if (rewardBlock) return; // монтируем единожды (meta стабильна после verified)
    if (!getClientPrefs().showRewardScreen) return; // выключено в настройках
    const submit = s.dailySubmitResult;
    if (!submit || submit.status !== 'verified' || !submit.meta) return;
    const area = root.querySelector<HTMLElement>('.result-reward-area');
    if (!area) return;
    area.hidden = false;
    rewardBlock = mountRewardBlock(area, {
      summary: submit.meta,
      mode: s.mode,
      score: submit.scoreVerified,
      // isDaily → разбивка золота показывает множитель «×5 доска дня» (item 5).
      isDaily,
    });
    // reward-ряд занял высоту → board-track постамента стал короче → пере-fit доски,
    // чтобы снимок ужался и не наезжал на блок добычи (P1 visibility > P4, §0.A).
    if (boardAreaFit) boardAreaFit.refit();
  };

  // --- Модальный оверлей «Открыт новый помощник!» (редизайн 2026-06-20, тикет helper-unlock-
  // overlay-redesign). Тонкая ветка: result-screen МОНТИРУЕТ модуль lib/helper-unlock-overlay
  // (вся логика — dialog/очередь/reveal/немедленный бейдж — в модуле, §1 ствол→ветки). Читает
  // server-truth `submit.meta.progression.gnome_unlocks` (I-Gn4 — не пересчитываем открытия).
  // НЕ под `showRewardScreen` (это про блок добычи): оверлей+бейдж — отдельный UX, открытие
  // помощника всегда заметно. Пусто/absent → модуль no-op. Монтируем ОДИН раз (meta стабильна
  // после verified). Переход на «Помощники» — store.showGnomes() (тот же путь, что рейл-пункт). ---
  let helperOverlay: HelperUnlockOverlayHandle | null = null;
  const maybeMountHelperOverlay = (s: GameStoreState): void => {
    if (helperOverlay) return; // единожды
    const submit = s.dailySubmitResult;
    if (!submit || submit.status !== 'verified' || !submit.meta) return;
    // Передаём всю server-truth сводку — модуль сам читает gnome_unlocks (I-Gn4) и no-op'ит,
    // если открытий нет. Экран НЕ читает gnome_unlocks / НЕ строит dialog (§1 ствол→ветки, AC-1).
    helperOverlay = mountHelperUnlockOverlay({ summary: submit.meta });
  };

  // --- Окошко разблокировки ачивок над доской (тикет fe-achievement-result-window, owner-
  // decisions 2026-06-22). Тонкая ветка: монтируем модуль lib/achievement-result-window
  // (вся логика — плавающая карточка / список разом / bounded-overflow «+ ещё N» / auto-
  // dismiss / плавный уход — в модуле, §1 ствол→ветки). Читает server-truth
  // `submit.meta.progression.achievements_unlocked` (I-3 — не пересчитываем). Пусто/absent →
  // модуль no-op. ТОТ ЖЕ seam, что helper-unlock (verified daily-submit meta). Якорь позиции —
  // boardView.frame (top-right рамки доски, R10); board-view не модифицируется. ---
  let achievementWindow: AchievementResultWindowHandle | null = null;
  const maybeMountAchievementResultWindow = (s: GameStoreState): void => {
    if (achievementWindow) return; // единожды
    if (!boardView) return; // нужен якорь рамки доски (R10)
    const submit = s.dailySubmitResult;
    if (!submit || submit.status !== 'verified' || !submit.meta) return;
    achievementWindow = mountAchievementResultWindow({
      summary: submit.meta,
      boardFrame: boardView.frame,
      // Демо-роут `?demo=achievementwindow` → persistent (owner осматривает / скриншот,
      // AC-DEMO.3); реальный поток конца партии → авто-уход 3000ms (autoDismiss default true).
      ...(isDemoAchievementWindow() ? { autoDismiss: false } : {}),
      // `&reduced=1` при демо → мгновенный показ без анимации (AC-DEMO.2 reduce-motion).
      ...(isDemoAchievementWindowReduced() ? { reduceMotion: true } : {}),
    });
  };

  // --- Динамика вердикта сервера (server-scored: daily + all_time): подписка на
  // submit-результат. Both use `dailySubmitResult` (the field is reused for the
  // non-daily submit verdict — §5.3 same surface). ---
  let unsubscribe: (() => void) | null = null;
  if (isServerScored) {
    applyDynamic(root, useGameStore.getState(), boardView);
    maybeMountReward(useGameStore.getState());
    maybeMountHelperOverlay(useGameStore.getState());
    maybeMountAchievementResultWindow(useGameStore.getState());
    unsubscribe = useGameStore.subscribe((cur, prev) => {
      if (cur.phase !== 'result') return;
      if (cur.dailySubmitResult !== prev.dailySubmitResult) {
        applyDynamic(root, cur, boardView);
        maybeMountReward(cur);
        maybeMountHelperOverlay(cur);
        maybeMountAchievementResultWindow(cur);
        autofit.refit();
      }
    });
  }

  // Снимаем reveal-gate через double-rAF (после settle доски-снимка/постамента) →
  // контент проявляется СРАЗУ целиком. Поздний async-вердикт (submit) обновляет добычу
  // уже на ВИДИМОМ экране — это не первый кадр перехода, мелькания пустоты не даёт.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      root.removeAttribute('data-mounting');
    }),
  );

  return {
    unmount(): void {
      if (unsubscribe) unsubscribe();
      if (helperOverlay) helperOverlay.unmount();
      if (achievementWindow) achievementWindow.unmount();
      if (rewardBlock) rewardBlock.unmount();
      if (boardAppearance) boardAppearance.detach();
      if (boardAreaFit) boardAreaFit.detach();
      if (boardView) boardView.unmount();
      autofit.detach();
      titleFit.detach();
      actionRail.detach();
      while (popovers.length > 0) popovers.pop()!.unmount();
      host.innerHTML = '';
    },
  };
}

// =============================================================================
// [ВРЕМЕННАЯ ДИАГНОСТИКА realme — снять после починки] on-screen замер для фото
// owner'ом. Активна только при ?vfitdebug=1. Показывает фактические railH/need/
// state/видимость вердикта НА РЕАЛЬНОМ УСТРОЙСТВЕ (headless не воспроизводит коллапс).
// =============================================================================
function attachVfitDebug(root: HTMLElement): void {
  // Флаг захвачен на boot в dev-flags (URL чистится приложением до монтирования итога).
  if (!isVfitDebug()) return;
  const badge = document.createElement('div');
  badge.style.cssText =
    'position:fixed;left:4px;top:4px;z-index:99999;background:#000;color:#0f0;' +
    'font:13px/1.4 monospace;padding:6px 9px;border-radius:6px;white-space:pre;' +
    'pointer-events:none;box-shadow:0 0 0 2px #0f0';
  document.body.appendChild(badge);
  const rail = root.querySelector<HTMLElement>('.result-rail');
  const header = root.querySelector<HTMLElement>('.result-header');
  const sec = root.querySelector<HTMLElement>('.result-secondary');
  const actions = root.querySelector<HTMLElement>('.result-actions');
  const status = root.querySelector<HTMLElement>('#result-status');
  const tick = (): void => {
    if (!rail || !actions) return;
    const railH = Math.round(rail.clientHeight);
    const gap = parseFloat(getComputedStyle(rail).rowGap) || 0;
    const need = Math.round(
      (header?.offsetHeight ?? 0) + (sec?.scrollHeight ?? 0) + actions.offsetHeight + 2 * gap,
    );
    const compact = rail.classList.contains('rail-is-compact');
    const tight = actions.classList.contains('rail-actions-tight');
    const collapsed = sec?.classList.contains('rail-secondary-collapsed') ?? false;
    const visible =
      !!status &&
      !status.hasAttribute('hidden') &&
      getComputedStyle(status).display !== 'none' &&
      status.getBoundingClientRect().height > 1;
    const state = compact ? 'COMPACT' : collapsed ? 'COLLAPSE' : tight ? 'TIGHT' : 'NORMAL';
    badge.textContent =
      `vw×vh=${window.innerWidth}×${window.innerHeight}\n` +
      `layout=${document.documentElement.dataset.layout}\n` +
      `railH=${railH}  need=${need}  (${need <= railH ? '+' + (railH - need) : '-' + (need - railH)})\n` +
      `state=${state}\n` +
      `вердикт виден=${visible ? 'ДА' : 'НЕТ'}`;
  };
  tick();
  const id = window.setInterval(tick, 350);
  window.setTimeout(() => {
    window.clearInterval(id);
    badge.remove();
  }, 600000);
}

// =============================================================================
// Template
// =============================================================================

function template(s: GameStoreState): string {
  const isDaily = s.context.kind === 'daily';
  const isAllTime = s.context.kind === 'all_time';
  const isServerScored = isDaily || isAllTime;
  const title = isDaily ? t('game.result.titleDaily') : t('game.result.titleCasual');

  // Вторичный ряд под доской — для server-scored партий (доска дня + не-daily):
  // статус-строка вердикта сервера («Зачтено: счёт N» зелёным на успех). casual
  // вторичного ряда не имеет (нет серверного зачёта). Очки/Ходы — на плашке доски.
  const secondary = isServerScored
    ? `
      <div class="result-secondary postament-secondary">
        <div class="result-status" id="result-status" hidden></div>
      </div>`
    : '';

  // Reward block area (meta «Ювелир» Срез 1b, meta-jeweler-surfaces.md §4.1) — host
  // для lib/reward-block. Только server-scored партии (daily/all_time несут серверную
  // `meta`-сводку; casual — локальная, добычи нет). Заполняется реактивно, когда
  // приходит verified-результат с `meta` (см. wireRewardBlock). Пустой при старте.
  const rewardArea = isServerScored
    ? `<div class="result-reward-area postament-reward" hidden></div>`
    : '';

  const actions = isDaily
    ? dailyActionsMarkup()
    : isAllTime
      ? allTimeActionsMarkup()
      : casualActionsMarkup();

  return `
    <div class="result-rail postament-rail">
      <div class="result-header postament-header">
        <div class="result-titleline postament-titleline">
          <div class="result-title postament-title screen-title">${escapeHtml(title)}</div>
        </div>
      </div>

      ${secondary}

      ${rewardArea}

      <div class="result-actions postament-actions">
        ${actions}
      </div>

      <div class="rail-compact" aria-hidden="true"></div>
    </div>

    <!-- Финальную доску монтирует ЕДИНЫЙ модуль board-view (mountBoardView) в эту
         область при mount — отдельной разметки доски здесь нет (доска = один объект). -->
    <div class="result-board-area postament-board"></div>
  `;
}

// Минимум 2 кнопки (owner-decision 2026-05-30): на result — только действия про ЭТУ
// партию. Навигация («К лидерборду», «К доске дня») и вход — в будущий глобальный
// рейл (отдельная задача). 2 кнопки = один ряд → доска не сдвигается (вертикаль).
function casualActionsMarkup(): string {
  return `
    <button class="btn primary" id="btn-replay" type="button">${t('game.replay')}</button>
    <button class="btn" id="btn-again" type="button">${t('game.result.btnAgainCasual')}</button>
  `;
}

function dailyActionsMarkup(): string {
  return `
    <button class="btn primary" id="btn-replay" type="button">${t('game.replay')}</button>
    <button class="btn result-action--share" id="btn-share" type="button">
      <span>${t('game.result.btnShare')}</span>
      <span class="result-share-info-slot" aria-hidden="false"></span>
    </button>
    <!-- «Повторить отправку» — скрыта; всплывает только при сбое отправки (редко). -->
    <button class="btn" id="btn-retry" type="button" hidden>${t('game.result.btnRetry')}</button>
  `;
}

/**
 * Non-daily (all_time) result actions (extra-modes §5.3): replay / play again
 * (same mode) / share (always open — non-daily replays have no gate) / save-to-Book
 * (placeholder — Книга реплеев is a separate spec, §3.8). «Повторить отправку»
 * surfaces only on a non-consuming failure.
 *
 * P4 (owner-playtest brief-39, fix-cycle T-39R-A3): «К лидерборду режима» REMOVED —
 * the mode leaderboard now lives ONLY in the «Лидерборд» section (4-selector
 * plashka, §5.4). Result CTAs are about THIS game only (AC-3.1).
 */
function allTimeActionsMarkup(): string {
  return `
    <button class="btn primary" id="btn-replay" type="button">${t('game.replay')}</button>
    <button class="btn" id="btn-again" type="button">${t('game.result.btnAgainAllTime')}</button>
    <button class="btn result-action--share" id="btn-share" type="button">
      <span>${t('game.result.btnShare')}</span>
    </button>
    <button class="btn" id="btn-save-book" type="button" disabled aria-disabled="true">${t('game.result.btnSaveBook')}</button>
    <button class="btn" id="btn-retry" type="button" hidden>${t('game.result.btnRetry')}</button>
  `;
}

/**
 * Описательная копия «как считается добыча» (item 3 owner-playtest 2026-06-06: «шкала
 * Осколки непонятна — объяснить, что это и откуда»; §4.1 «Descriptive (→`?`): как
 * считается золото / откуда камень / что такое перелив»). Числа-якоря (пороги/множитель)
 * — из config/meta (no-hardcode §4): золото — `GOLD_PER_POINT_THRESHOLD`, осколки —
 * `SHARDS_MIN_CLEAR` + `SHARDS_MODE_BASE`, множитель — `DAILY_GOLD_MULTIPLIER`. Это
 * descriptive copy (за «?»/ⓘ), не must-visible label.
 */
function describeLoot(mode: GameMode, isDaily: boolean): string {
  const goldThreshold = GOLD_PER_POINT_THRESHOLD[mode];
  const shardBase = SHARDS_MODE_BASE[mode];
  const lines = [t('game.result.lootDescGold', { n: formatNumber(goldThreshold) })];
  if (isDaily) {
    lines.push(t('game.result.lootDescDailyMult', { n: formatNumber(DAILY_GOLD_MULTIPLIER) }));
  }
  lines.push(
    t('game.result.lootDescShards', {
      n: formatNumber(SHARDS_MIN_CLEAR),
      base: formatNumber(shardBase),
    }),
  );
  lines.push(t('game.result.lootDescGem'));
  return lines.join('\n');
}

/** Описательный текст для «?»/ⓘ-попапа (контекст-зависимый). */
function describeResult(s: GameStoreState): string {
  const finishLabel = s.finishReason ? finishReasonLabel(s.finishReason) : t('game.result.finishDone');
  const isDaily = s.context.kind === 'daily';
  const isServerScored = isDaily || s.context.kind === 'all_time';
  const parts: string[] = [];
  if (isDaily) parts.push(resultDescriptiveCopy());
  parts.push(t('game.result.descFinishReason', { reason: finishLabel }));
  // Пояснение добычи — только для server-scored партий с включённым блоком добычи
  // (у них есть мета-сводка; casual добычи не имеет — пояснять нечего, item 3).
  if (isServerScored && getClientPrefs().showRewardScreen) {
    parts.push(describeLoot(s.mode, isDaily));
  }
  return parts.join('\n\n');
}

// =============================================================================
// Dynamic verdict (daily) — score + status + retry, обновляется по submit
// =============================================================================

function applyDynamic(root: HTMLElement, s: GameStoreState, boardView: BoardViewHandle | null): void {
  // Server-scored = daily OR all_time (both publish a verified verdict via
  // `dailySubmitResult`). Casual has no server verdict.
  if (s.context.kind !== 'daily' && s.context.kind !== 'all_time') return;
  const submit = s.dailySubmitResult;

  // Счёт: server-verified если есть, иначе локальный (pending). Показывается на
  // плашке доски (единый модуль) — синхронизируем с verified значением.
  const displayScore =
    submit && submit.status === 'verified' ? submit.scoreVerified : getScore(s);
  if (boardView) boardView.setScore(displayScore);

  const statusEl = root.querySelector<HTMLElement>('#result-status');
  const retryBtn = root.querySelector<HTMLButtonElement>('#btn-retry');

  const verdict = describeVerdict(submit);
  if (statusEl) {
    statusEl.textContent = verdict.text;
    statusEl.className = `result-status result-status--${verdict.tone}`;
    statusEl.hidden = verdict.text === '';
  }
  // Retry — только когда попытка НЕ списана (network / rate_limited).
  const canRetry =
    submit !== null && (submit.status === 'network' || submit.status === 'rate_limited');
  if (retryBtn) retryBtn.hidden = !canRetry;

  // Терминальная ошибка — продублировать тостом (заметность без баннер-строки).
  if (verdict.tone === 'err' && verdict.toast) showToast(verdict.text);
}

interface Verdict {
  text: string;
  tone: 'pending' | 'ok' | 'neutral' | 'err';
  toast: boolean;
}

function describeVerdict(submit: SubmitResult | null): Verdict {
  if (submit === null) return { text: t('game.result.verdict.pending'), tone: 'pending', toast: false };
  switch (submit.status) {
    case 'verified':
      return {
        text: t('game.result.verdict.verified', { score: formatNumber(submit.scoreVerified) }),
        tone: 'ok',
        toast: false,
      };
    case 'duplicate_attempt':
      return { text: t('game.result.verdict.duplicate'), tone: 'neutral', toast: false };
    case 'engine_mismatch':
      return { text: t('game.result.verdict.engineMismatch'), tone: 'err', toast: true };
    case 'rate_limited':
      return { text: t('game.result.verdict.rateLimited'), tone: 'err', toast: true };
    case 'rejected': {
      const label = rejectReasonLabel(submit.reason);
      return { text: t('game.result.verdict.rejected', { reason: label }), tone: 'err', toast: true };
    }
    default:
      return { text: t('game.result.verdict.error'), tone: 'err', toast: true };
  }
}

// =============================================================================
// Action rail — общий kit (autofit + компакт в узком rail), result-контент попапов
// =============================================================================

function attachResultActionRail(root: HTMLElement, isDaily: boolean): CompactRailHandle {
  const rail = root.querySelector<HTMLElement>('.result-rail');
  const actionsContainer = root.querySelector<HTMLElement>('.result-actions');
  const compactHost = root.querySelector<HTMLElement>('.rail-compact');
  if (!rail || !actionsContainer || !compactHost) {
    return { detach(): void {} };
  }
  // .action-rail — CSS-хук вертикальной формы кнопок в rail (action-rail.css,
  // только `:root[data-layout='rail']`; в stacked действует наша 2-кол сетка).
  actionsContainer.classList.add('action-rail');
  const actionButtons = Array.from(
    actionsContainer.querySelectorAll<HTMLElement>('.btn'),
  ).filter((b) => !b.hidden);

  const compact = attachCompactRail(root, {
    rail,
    compactHost,
    actionButtons,
    compactClass: 'rail-is-compact',
    floorPx: FS_BUTTON_MIN_PX,
    infoTitle: isDaily ? t('game.result.titleDaily') : t('game.result.titleCasual'),
    buildInfoBody: () => buildRailInfoBody(useGameStore.getState()),
    buildActionsBody: (close) => buildRailActionsBody(useGameStore.getState(), close),
  });

  // Вертикальная подгонка (ландшафт): вторичная строка-вердикт «Зачтено: N» при
  // нехватке высоты СНАЧАЛА ужимает зазор кнопок, только потом прячется (owner-directive
  // 2026-06-05). Есть только у server-scored итога (daily/all_time — у них secondary).
  const secondary = root.querySelector<HTMLElement>('.result-secondary');
  const verticalFit = secondary
    ? attachRailVerticalFit(root, {
        rail,
        secondary,
        actions: actionsContainer,
        tightClass: 'rail-actions-tight',
        collapsedClass: 'rail-secondary-collapsed',
        compactClass: 'rail-is-compact',
      })
    : null;

  return {
    detach(): void {
      compact.detach();
      if (verticalFit) verticalFit.detach();
      actionsContainer.classList.remove('action-rail');
    },
  };
}

/**
 * ⓘ-попап (узкий rail): заголовок + KPI + (server-scored) статус + ДОБЫЧА ПАРТИИ.
 *
 * item 7 (owner-playtest 2026-06-06): в компакт-ландшафте rail сворачивается в ⓘ/☰ →
 * блок добычи в rail-колонке становится недоступен, игрок терял данные о золоте/
 * осколках/камнях. Фикс: ⓘ-попап несёт добычу партии текстом (золото/осколки/камни) +
 * пояснение «как считается» (descriptive §0.E) — данные не теряются при свёртке.
 */
function buildRailInfoBody(s: GameStoreState): HTMLElement {
  const isDaily = s.context.kind === 'daily';
  // Server-scored = daily ИЛИ all_time — оба несут verified-сводку в `dailySubmitResult`
  // (поле переиспользовано, §5.3). Прежде здесь читался submit ТОЛЬКО для daily → у
  // all_time добыча/вердикт в ⓘ не показывались. Берём для обоих.
  const isServerScored = isDaily || s.context.kind === 'all_time';
  const wrap = document.createElement('div');

  const title = document.createElement('div');
  title.className = 'rail-popover-text-title';
  title.textContent = isDaily ? t('game.result.titleDaily') : t('game.result.titleCasual');

  const stats = document.createElement('div');
  stats.className = 'rail-popover-text-stats';
  const submit = isServerScored ? s.dailySubmitResult : null;
  const score =
    submit && submit.status === 'verified' ? submit.scoreVerified : getScore(s);
  const mkStat = (k: string, v: string): HTMLElement => {
    const span = document.createElement('span');
    span.append(document.createTextNode(`${k}: `));
    const b = document.createElement('b');
    b.textContent = v;
    span.append(b);
    return span;
  };
  stats.append(
    mkStat(t('game.result.kpiScore'), formatNumber(score)),
    mkStat(t('game.result.kpiMoves'), String(s.moves.length)),
  );
  // (Таймер «До новой доски» убран — owner-directive 2026-05-30; живёт на превью.)

  wrap.append(title, stats);
  if (isServerScored) {
    const v = describeVerdict(submit);
    if (v.text) {
      const status = document.createElement('div');
      status.className = `rail-popover-text-sub result-status--${v.tone}`;
      status.textContent = v.text;
      wrap.append(status);
    }
    // Добыча партии (item 7) — текстом, когда есть verified-сводка с мета И блок добычи
    // включён в настройках. В развёрнутом rail добыча видна блоком; в компакте — здесь.
    if (
      submit &&
      submit.status === 'verified' &&
      submit.meta &&
      getClientPrefs().showRewardScreen
    ) {
      wrap.append(buildRailLootSummary(submit.meta));
      const note = document.createElement('div');
      note.className = 'rail-popover-text-sub';
      note.style.whiteSpace = 'pre-line';
      note.textContent = describeLoot(s.mode, isDaily);
      wrap.append(note);
    }
  }
  return wrap;
}

/**
 * Сводка добычи партии текстом для компакт-ⓘ (item 7) — золото / осколки / самоцветы.
 * Те же server-truth числа, что блок добычи; здесь — текстовая форма для попапа узкого
 * rail (блок-визуал туда не помещается). Перенос строк — стиль попапа.
 */
function buildRailLootSummary(sum: MetaFaucetSummary): HTMLElement {
  const block = document.createElement('div');
  block.className = 'rail-popover-text-stats';
  const mkLoot = (k: string, v: string): HTMLElement => {
    const span = document.createElement('span');
    span.append(document.createTextNode(`${k}: `));
    const b = document.createElement('b');
    b.textContent = v;
    span.append(b);
    return span;
  };
  const items: HTMLElement[] = [
    mkLoot(t('game.result.lootGold'), `+${formatNumber(sum.gold_earned)}`),
    mkLoot(
      t('game.result.lootShards'),
      sum.shards_earned > 0 ? `+${formatNumber(sum.shards_earned)}` : '0',
    ),
  ];
  if (sum.gems_dropped > 0) {
    items.push(mkLoot(t('game.result.lootGems'), formatNumber(sum.gems_dropped)));
  }
  block.append(...items);
  return block;
}

/** ☰-попап (узкий rail): действия полными лейблами (те же store-действия). */
function buildRailActionsBody(s: GameStoreState, close: () => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'rail-popover-actions';
  const mk = (label: string, onClick: () => void, primary = false): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = primary ? 'btn primary' : 'btn';
    b.textContent = label;
    b.addEventListener('click', () => {
      onClick();
      close();
    });
    return b;
  };

  if (s.context.kind === 'daily') {
    const submit = s.dailySubmitResult;
    const canRetry =
      submit !== null && (submit.status === 'network' || submit.status === 'rate_limited');
    wrap.append(
      mk(t('game.replay'), () => useGameStore.getState().showReplay(), true),
      mk(t('game.result.btnShare'), () => {
        void shareOwnReplay();
      }),
    );
    if (canRetry) wrap.append(mk(t('game.result.btnRetry'), () => void retrySubmit()));
  } else if (s.context.kind === 'all_time') {
    const submit = s.dailySubmitResult;
    const canRetry =
      submit !== null && (submit.status === 'network' || submit.status === 'rate_limited');
    wrap.append(
      mk(t('game.replay'), () => useGameStore.getState().showReplay(), true),
      mk(t('game.result.btnAgainAllTime'), () => void playAgainFromResult()),
      mk(t('game.result.btnShare'), () => {
        void shareOwnReplay();
      }),
      // P4 (T-39R-A3): «К лидерборду режима» removed — mode leaderboard is in the
      // «Лидерборд» section now (§5.4). Save-to-Book is a future placeholder (not
      // wired into the compact menu yet).
    );
    if (canRetry) wrap.append(mk(t('game.result.btnRetry'), () => void retrySubmit()));
  } else {
    wrap.append(
      mk(t('game.replay'), () => useGameStore.getState().showReplay(), true),
      mk(t('game.result.btnAgainCasual'), () => void playAgainFromResult()),
    );
  }
  return wrap;
}

// =============================================================================
// Wiring
// =============================================================================

function wire(root: HTMLElement): void {
  root.querySelector<HTMLButtonElement>('#btn-replay')?.addEventListener('click', () => {
    useGameStore.getState().showReplay();
  });

  // «Играть ещё» / «Сыграть ещё»: casual → fresh local game; all_time → fresh
  // non-daily game of the SAME mode (new server session). Shared helper keeps the
  // restart rule in one place (mirrors board-screen `restartByContext`).
  root.querySelector<HTMLButtonElement>('#btn-again')?.addEventListener('click', () => {
    void playAgainFromResult();
  });

  root.querySelector<HTMLButtonElement>('#btn-daily')?.addEventListener('click', () => {
    useGameStore.getState().showDaily();
  });

  // (P4 / T-39R-A3: «#btn-leaderboard» «К лидерборду режима» removed from the result
  // — the mode leaderboard lives in the «Лидерборд» section now, §5.4.)

  // «Поделиться» — копирует ссылку на свой реплей `/r/<short_id>` (AC-5). Ссылка
  // приходит от сервера в submit-результате (`replayUrl`). До прихода verified —
  // ссылки ещё нет (партия не зачтена), показываем поясняющий toast. «!»-иконка
  // внутри кнопки сама не активирует Share (stopPropagation в info-popover).
  root.querySelector<HTMLButtonElement>('#btn-share')?.addEventListener('click', () => {
    void shareOwnReplay();
  });

  root.querySelector<HTMLButtonElement>('#btn-retry')?.addEventListener('click', () => {
    void retrySubmit();
  });
}

// =============================================================================
// Share own replay link (AC-5) — copies `/r/<short_id>` to the clipboard
// =============================================================================

/**
 * Copy the OWN replay link (`/r/<short_id>`) to the clipboard (replays-ghosts.md
 * §2.5 / AC-5). The link is the server-issued `replayUrl` from the verified
 * submit result. Before the verified verdict lands there is no public link yet
 * (the attempt isn't scored) — surface a explanatory toast instead of copying a
 * stale value. Receivers see the teaser today / playable after rollover
 * (foundational invariant §3A — enforced server-side, not here).
 */
async function shareOwnReplay(): Promise<void> {
  const s = useGameStore.getState();
  const submit = s.dailySubmitResult;
  const replayUrl =
    submit && submit.status === 'verified' ? submit.replayUrl : '';
  if (!replayUrl) {
    showToast(t('game.result.toastNoLink'));
    return;
  }
  const outcome = await copyShareLink(replayUrl);
  showToast(outcome.ok ? t('game.result.toastCopied') : t('game.result.toastCopyFail'));
}

/**
 * «Играть ещё» / «Сыграть ещё» from the result screen. casual → fresh local game;
 * all_time → open a FRESH pre-start PREVIEW of the SAME mode (new server session via
 * `POST /game/start`, lazy-on-preview T-A; «Начать» begins it). On all_time start
 * failure, `openModePreview` toasts and we bounce to the mode-selector.
 */
async function playAgainFromResult(): Promise<void> {
  const s = useGameStore.getState();
  if (s.context.kind === 'all_time') {
    const ok = await openModePreview(s.mode);
    if (!ok) useGameStore.getState().showModeSelect();
    return;
  }
  useGameStore.getState().startNewGame();
  useGameStore.setState({ phase: 'board' });
}

// =============================================================================
// Daily retry submit (несущая логика, без изменений по сути)
// =============================================================================

async function retrySubmit(): Promise<void> {
  const s = useGameStore.getState();
  if (!s.finishReason) return;
  const reason = s.finishReason;

  // all_time retry — re-submit against the same server session (extra-modes). No
  // daily-cache discipline (non-daily has no per-day cache).
  if (s.context.kind === 'all_time') {
    const ctx = s.context;
    s.setDailySubmitResult(null);
    let result: SubmitResult;
    try {
      result = await submitFinishedAllTimeGame({
        context: { kind: 'all_time', sessionId: ctx.sessionId },
        seed: s.seed,
        moves: s.moves.slice(),
        scoreClaimed: getScore(s),
        linesClaimed: getLines(s),
        finishReason: reason,
        mode: s.mode,
      });
    } catch {
      result = { status: 'network', attempt: 0 };
    }
    useGameStore.getState().setDailySubmitResult(result);
    return;
  }

  if (s.context.kind !== 'daily') return;
  s.setDailySubmitResult(null);
  const ctx = s.context;

  let result: SubmitResult;
  try {
    result = await submitFinishedDailyGame({
      context: { kind: 'daily', dateOfDay: ctx.dateOfDay },
      seed: s.seed,
      moves: s.moves.slice(),
      scoreClaimed: getScore(s),
      linesClaimed: getLines(s),
      finishReason: reason,
      mode: 'predict',
    });
  } catch {
    result = { status: 'network', attempt: 0 };
  }

  useGameStore.getState().setDailySubmitResult(result);

  // Снимок финальной доски (core-daily-board.md §2.3-DONE) — стор ещё держит
  // финальное состояние (retry с экрана результата). Кладётся в `played` на
  // verified-пути → done-state рисует доску мгновенно из кэша. getGrid непустой.
  const finalGrid = getGrid(s);
  const snapshot = finalGrid
    ? snapshotFinalBoard(finalGrid, getNext(s), s.moves.length, reason)
    : undefined;

  // Кэш дня — shared discipline (lib/daily-finish): `played` только когда сервер
  // списал попытку (verified / duplicate / post-pre-claim reject); для
  // rate_limited / engine_mismatch / network — НЕ кэшировать (попытка доступна,
  // in-progress остаётся для retry). Тот же модуль, что board-screen / trigger B.
  await applyDailyCacheDiscipline(ctx.dateOfDay, result, snapshot);
}

// =============================================================================
// Helpers
// =============================================================================

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}
